import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAircraftManagement, useFlightManagement } from '../../hooks';
import { useAppSelector } from '../../../store/hooks';
import AirportDropdown from '../../../components/AirportDropdown/AirportDropdown';
import { createFlight, saveDraft, updateFlight, fetchFlightById, rescheduleFlight,clearError } from '../../../store/slices/flightManagementSlice';
import aircraftService from '../../../services/aircraftService';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import { showSuccessToast, showErrorToast } from '../../../utils/messageHelper';
import { useDispatch } from 'react-redux';
import FlightManagementTable from '../../components/FlightManagementTable/FlightManagementTable';
import toast from 'react-hot-toast';

const FlightFormPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { airlinerId } = useParams();
  const { aircraftId, flightId } = useParams();
  const location = useLocation();
  const { aircraft: aircrafts, loadAircraft } = useAircraftManagement();
  const { user } = useAppSelector((state) => state.auth);
  const { currentFlight, flights: allFlights, loading: flightLoading, error: flightError } = useAppSelector((state) => state.flightManagement);
  const { loadFlights } = useFlightManagement();

  // Determine if we're in edit mode
  const isEditMode = !!flightId;

  const [formData, setFormData] = useState({
    registrationNumber: '',
    flightNumber: '',
    departureAirport: '',
    departureDateTime: '',
    arrivalAirport: '',
    arrivalDateTime: '',
    blockPrice: '',
    seatAvailable: '',
    seatPrice: '',
    blockEmptyPrice: '',
    flightTime: '',
    blockEmptyLeg: false
  });

  const [aircraft, setAircraft] = useState(null);
  const [selectedAircraftId, setSelectedAircraftId] = useState(aircraftId || '');
  const [errors, setErrors] = useState({});
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  // Flight management table state (removing redundant local flights state)
  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState({
    regNo: true,
    flightNo: true,
    depAirport: true,
    etd: true,
    arrAirport: true,
    eta: true,
    belPrice: true,
    blPrice: true,
    seatPrice: true,
    seats: true,
    flightTime: true,
    status: true,
    action: true
  });

  const toLocalInput = (utcString) => {
    if (!utcString) return '';
    const date = new Date(utcString);
    if (Number.isNaN(date.getTime())) return '';

    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Load flight details if in edit mode
  useEffect(() => {
    if (isEditMode && flightId) {
      dispatch(fetchFlightById(flightId));
    }
  }, [isEditMode, flightId, dispatch]);

  // Populate form when flight data is loaded (edit mode)
  useEffect(() => {
    if (isEditMode && currentFlight) {
      setFormData({
        registrationNumber: currentFlight.registration_number || '',
        flightNumber: currentFlight.flight_number || '',
        departureAirport: currentFlight.departure_airport || '',
        departureDateTime: toLocalInput(currentFlight.departure_date_time),
        arrivalAirport: currentFlight.arrival_airport || '',
        arrivalDateTime: toLocalInput(currentFlight.arrival_date_time),
        blockPrice: currentFlight.block_empty_price > 0 ? currentFlight.block_empty_price : (currentFlight.block_price || ''),
        seatAvailable: currentFlight.seat_total || '',
        seatPrice: currentFlight.seat_price || '',
        blockEmptyPrice: currentFlight.block_empty_price || '',
        flightTime: currentFlight.flight_time || '',
        blockEmptyLeg: currentFlight.block_empty_price > 0
      });
    }
  }, [isEditMode, currentFlight]);

  // Load all user's aircraft if no aircraftId in URL (add mode)
  useEffect(() => {
    if (!isEditMode && !aircraftId && user?.id) {
      loadAircraft({ userId: user.id });
    }
    // Also load all flights for duplicate validation in Add mode
    if (!isEditMode) {
      if (location.pathname.startsWith('/admin') && aircraftId) {
        // Admin mode: Load flights for specific aircraft
        loadFlights({ aircraft_id: aircraftId });
      } else if (user?.id) {
        // Airliner mode: Load all flights for the user
        loadFlights({ userId: user.id });
      }
    }
  }, [isEditMode, aircraftId, user?.id, location.pathname, loadAircraft, loadFlights]);

  // Fetch aircraft details when aircraftId changes (add mode)
  useEffect(() => {
    if (!isEditMode && aircraftId) {
      const fetchAircraftDetails = async () => {
        try {
          const response = await aircraftService.getAircraftById(aircraftId);
          const aircraftData = response.data?.aircraft || response.data?.data || response.data;
          setAircraft(aircraftData);
          setFormData(prev => ({
            ...prev,
            registrationNumber: aircraftData?.registration_number || '',
            seatAvailable: ''
          }));
          setSelectedAircraftId(aircraftId);
        } catch (error) {
          // Failed to fetch aircraft details
        }
      };
      fetchAircraftDetails();
    }
  }, [isEditMode, aircraftId]);

  const toUTCISOString = (localDateTime) => {
    if (!localDateTime) return null;

    // datetime-local value is wall-clock time in the user's browser timezone
    const [datePart, timePart] = localDateTime.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    return localDate.toISOString();
  };

  // Handle aircraft selection from dropdown (add mode only)
  const handleAircraftChange = (e) => {
    const selectedId = e.target.value;
    setSelectedAircraftId(selectedId);

    if (selectedId) {
      const selectedAircraft = aircrafts.find(a => a.id.toString() === selectedId);
      if (selectedAircraft) {
        setAircraft(selectedAircraft);
        setFormData(prev => ({
          ...prev,
          registrationNumber: selectedAircraft.registration_number || '',
          seatAvailable: ''
        }));
      }
    } else {
      setAircraft(null);
      setFormData(prev => ({
        ...prev,
        registrationNumber: '',
        seatAvailable: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // For seat-related fields, only allow whole numbers
    if ((name === 'seatAvailable' || name === 'seatPrice') && type === 'number') {
      // Remove any decimal points
      const intValue = value.replace(/[.,]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: intValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const calculateSeatPrice = () => {
    const blockPrice = parseFloat(formData.blockPrice) || 0;
    const seatAvailable = parseInt(formData.seatAvailable) || 0;

    if (blockPrice > 0 && seatAvailable > 0) {
      const price = (blockPrice / seatAvailable).toFixed(2);
      setFormData(prev => ({ ...prev, seatPrice: price }));
    }
  };

  const calculateFlightTime = () => {
    if (formData.departureDateTime && formData.arrivalDateTime) {
      const dep = new Date(formData.departureDateTime);
      const arr = new Date(formData.arrivalDateTime);
      const diff = arr - dep;

      if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setFormData(prev => ({
          ...prev,
          flightTime: `${hours}:${minutes.toString().padStart(2, '0')}`
        }));
      }
    }
  };

  useEffect(() => {
    calculateSeatPrice();
  }, [formData.blockPrice, formData.seatAvailable]);

  useEffect(() => {
    calculateFlightTime();
  }, [formData.departureDateTime, formData.arrivalDateTime]);

  const handleBlockEmptyLegChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      blockEmptyLeg: checked,
      blockEmptyPrice: checked ? prev.blockPrice : ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isEditMode && !formData.registrationNumber) newErrors.registrationNumber = 'Registration number is required';
    if (!formData.flightNumber) newErrors.flightNumber = 'Flight number is required';
    if (!formData.departureAirport) newErrors.departureAirport = 'Departure airport is required';
    if (!formData.departureDateTime) newErrors.departureDateTime = 'Departure date time is required';
    if (!formData.arrivalAirport) newErrors.arrivalAirport = 'Arrival airport is required';
    if (!formData.arrivalDateTime) newErrors.arrivalDateTime = 'Arrival date time is required';
    if (!formData.blockPrice) newErrors.blockPrice = 'Block price is required';
    if (!formData.blockEmptyLeg && !formData.seatAvailable) newErrors.seatAvailable = 'Seat available is required';
    
    // Check if seat available exceeds aircraft capacity
    if (!formData.blockEmptyLeg && formData.seatAvailable && aircraft?.seating_capacity) {
      if (parseInt(formData.seatAvailable) > parseInt(aircraft.seating_capacity)) {
        newErrors.seatAvailable = `Please enter a value less than or equal to ${aircraft.seating_capacity}.`;
      }
    }

    // Check if departure date/time is in the future
    if (formData.departureDateTime) {
      const depDate = new Date(formData.departureDateTime);
      const now = new Date();
      if (depDate <= now) {
        newErrors.departureDateTime = 'Departure date/time must be in the future';
      }
    }

    // Check if departure and arrival airports are the same
    if (formData.departureAirport && formData.arrivalAirport &&
      formData.departureAirport === formData.arrivalAirport) {
      newErrors.arrivalAirport = 'Arrival airport cannot be the same as departure airport';
    }

    // Check if arrival date/time is greater than departure date/time
    if (formData.departureDateTime && formData.arrivalDateTime) {
      const depDate = new Date(formData.departureDateTime);
      const arrDate = new Date(formData.arrivalDateTime);

      if (arrDate <= depDate) {
        newErrors.arrivalDateTime = 'Arrival date/time must be greater than departure date/time';
      }
    }

    // Check if flight time exceeds aircraft endurance (for both add and edit modes)
    if (aircraft && formData.flightTime) {
      const aircraftEndurance = aircraft.endurance || aircraft.max_endurance;
      if (aircraftEndurance) {
        const [flightHours, flightMinutes] = formData.flightTime.split(':').map(Number);
        const [enduranceHours, enduranceMinutes] = aircraftEndurance.split(':').map(Number);
        
        const flightTotalMinutes = (flightHours || 0) * 60 + (flightMinutes || 0);
        const enduranceTotalMinutes = (enduranceHours || 0) * 60 + (enduranceMinutes || 0);
        
        if (flightTotalMinutes > enduranceTotalMinutes) {
          newErrors.endurance = `The flight time exceeds the endurance of ${aircraftEndurance} for this aircraft.`;
        }
      }
    }

    // Check for duplicate flight (same flight number and departure time)
    if (formData.flightNumber && formData.departureDateTime) {
      const isDuplicate = allFlights.some(f => {
        // In Edit mode, ignore the current flight itself
        if (isEditMode && f.id === parseInt(flightId)) return false;

        // Check for same flight number (case-insensitive) and same departure time
        return f.flight_number?.toLowerCase() === formData.flightNumber.toLowerCase() &&
          f.departure_date_time?.slice(0, 16) === formData.departureDateTime;
      });

      if (isDuplicate) {
        newErrors.flightNumber = 'This flight number and departure time already exists';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFormForReschedule = () => {
    const newErrors = {};

    if (!formData.flightNumber) newErrors.flightNumber = 'Flight number is required';
    if (!formData.departureAirport) newErrors.departureAirport = 'Departure airport is required';
    if (!formData.departureDateTime) newErrors.departureDateTime = 'Departure date time is required';
    if (!formData.arrivalAirport) newErrors.arrivalAirport = 'Arrival airport is required';
    if (!formData.arrivalDateTime) newErrors.arrivalDateTime = 'Arrival date time is required';
    if (!formData.blockPrice) newErrors.blockPrice = 'Block price is required';
    if (!formData.blockEmptyLeg && !formData.seatAvailable) newErrors.seatAvailable = 'Seat available is required';
    
    // Check if seat available exceeds aircraft capacity
    if (!formData.blockEmptyLeg && formData.seatAvailable && aircraft?.seating_capacity) {
      if (parseInt(formData.seatAvailable) > parseInt(aircraft.seating_capacity)) {
        newErrors.seatAvailable = `Please enter a value less than or equal to ${aircraft.seating_capacity}.`;
      }
    }

    // Check if reschedule departure date/time is in the future
    if (formData.departureDateTime) {
      const depDate = new Date(formData.departureDateTime);
      const now = new Date();
      if (depDate <= now) {
        newErrors.departureDateTime = 'Departure date/time must be in the future';
      }
    }

    // Check if departure and arrival airports are the same
    if (formData.departureAirport && formData.arrivalAirport &&
      formData.departureAirport === formData.arrivalAirport) {
      newErrors.arrivalAirport = 'Arrival airport cannot be the same as departure airport';
    }

    // Check if arrival date/time is greater than departure date/time
    if (formData.departureDateTime && formData.arrivalDateTime) {
      const depDate = new Date(formData.departureDateTime);
      const arrDate = new Date(formData.arrivalDateTime);

      if (arrDate <= depDate) {
        newErrors.arrivalDateTime = 'Arrival date/time must be greater than departure date/time';
      }
    }

    // Check if flight time exceeds aircraft endurance
    if (aircraft && formData.flightTime) {
      const aircraftEndurance = aircraft.endurance || aircraft.max_endurance;
      if (aircraftEndurance) {
        const [flightHours, flightMinutes] = formData.flightTime.split(':').map(Number);
        const [enduranceHours, enduranceMinutes] = aircraftEndurance.split(':').map(Number);
        
        const flightTotalMinutes = (flightHours || 0) * 60 + (flightMinutes || 0);
        const enduranceTotalMinutes = (enduranceHours || 0) * 60 + (enduranceMinutes || 0);
        
        if (flightTotalMinutes > enduranceTotalMinutes) {
          newErrors.endurance = `The flight time exceeds the endurance of ${aircraftEndurance} for this aircraft.`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();

    // Always validate critical fields like same airport check, even for drafts
    const newErrors = {};

    // Check if departure and arrival airports are the same (always validate)
    if (formData.departureAirport && formData.arrivalAirport &&
      formData.departureAirport === formData.arrivalAirport) {
      newErrors.arrivalAirport = 'Arrival airport cannot be the same as departure airport';
    }

    // For non-draft submissions, run full validation
    if (!isDraft && !validateForm()) {
      return;
    }

    // If there are critical errors (like same airport), show them even for drafts
    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
      return;
    }

    // Store the submit data and show confirmation modal
    setPendingSubmitData({ isDraft });
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingSubmitData) return;

    const { isDraft } = pendingSubmitData;
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      // Get user's timezone offset in minutes
      const getTimezoneOffset = () => {
        return new Date().getTimezoneOffset();
      };

      const flightPayload = {
        flight_number: formData.flightNumber,
        departure_airport: formData.departureAirport,
        departure_date_time: toUTCISOString(formData.departureDateTime),
        arrival_airport: formData.arrivalAirport,
        arrival_date_time: toUTCISOString(formData.arrivalDateTime),
        block_price: formData.blockEmptyLeg ? 0 : (parseFloat(formData.blockPrice) || 0),
        seat_available: formData.blockEmptyLeg ? 0 : (parseInt(formData.seatAvailable) || 0),
        seat_price: formData.blockEmptyLeg ? 0 : (parseFloat(formData.seatPrice) || 0),
        block_empty_price: formData.blockEmptyLeg ? (parseFloat(formData.blockPrice) || 0) : 0,
        flight_time: formData.flightTime,
        not_operate_flight: isDraft ? undefined : 0
      };

      if (isEditMode) {
        // In edit mode, always save as draft (status: 'saved')
        flightPayload.status = 'saved';
        await dispatch(updateFlight({ id: flightId, data: flightPayload })).unwrap();
        showSuccessToast('flight.saved');
        if (location.pathname.startsWith('/admin')) {
          if (airlinerId) {
            navigate(`/admin/airliners/${airlinerId}/flights`);
          } else {
            navigate('/admin/admin-flights');
          }
        } else {
          navigate('/airliner/flights');
        }
      } else {
        // Create new flight
        flightPayload.aircraft_id = selectedAircraftId || aircraftId;
        flightPayload.registration_number = formData.registrationNumber;
        flightPayload.status = isDraft ? 'saved' : 'published';        

        // Add airliner_id for admin mode
        if (location.pathname.startsWith('/admin')) {
          // Try to get from URL params first, then from aircraft data
          flightPayload.airliner_id = airlinerId || aircraft?.user_id;
        }

        const resultAction = isDraft
          ? await dispatch(saveDraft(flightPayload)).unwrap()
          : await dispatch(createFlight(flightPayload)).unwrap();

        showSuccessToast(isDraft ? 'flight.saved' : 'flight.created');

        if (location.pathname.startsWith('/admin')) {
          if (airlinerId) {
            navigate(`/admin/airliners/${airlinerId}/flights`);
          } else {
            navigate('/admin/admin-flights');
          }
        } else {
          navigate('/airliner/flights');
        }
      }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error.message || 'Unknown error');
      toast.error(errorMessage);
      // Clear Redux error state so it doesn't show in the table
      dispatch(clearError());
    } finally {
      setIsSubmitting(false);
      setPendingSubmitData(null);
    }
  };

  const handleReschedule = () => {
    if (!validateFormForReschedule()) {
      // The individual field errors are already set in the errors state by validateFormForReschedule
      toast.error('Please correct the errors in the form before rescheduling.');
      return;
    }
    setShowRescheduleModal(true);
  };

  const handleRescheduleConfirm = async () => {
    setShowRescheduleModal(false);
    setIsSubmitting(true);

    try {
      const flightPayload = {
        flight_number: formData.flightNumber,
        departure_airport: formData.departureAirport,
        departure_date_time: toUTCISOString(formData.departureDateTime),
        arrival_airport: formData.arrivalAirport,
        arrival_date_time: toUTCISOString(formData.arrivalDateTime),
        block_price: formData.blockEmptyLeg ? 0 : (parseFloat(formData.blockPrice) || 0),
        seat_available: formData.blockEmptyLeg ? 0 : (parseInt(formData.seatAvailable) || 0),
        seat_price: formData.blockEmptyLeg ? 0 : (parseFloat(formData.seatPrice) || 0),
        block_empty_price: formData.blockEmptyLeg ? (parseFloat(formData.blockPrice) || 0) : 0,
        flight_time: formData.flightTime,
        status: 'published',
        not_operate_flight: 0
      };

      await dispatch(rescheduleFlight({ id: flightId, data: flightPayload })).unwrap();
      showSuccessToast('flight.rescheduled');
      if (location.pathname.startsWith('/admin')) {
        if (airlinerId) {
          navigate(`/admin/airliners/${airlinerId}/flights`);
        } else {
          navigate('/admin/admin-flights');
        }
      } else {
        navigate('/airliner/flights');
      }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error.message || 'Unknown error');
      toast.error(errorMessage);
      // Clear Redux error state so it doesn't show in the table
      dispatch(clearError());
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const handleDateTimeClick = (e) => {
    // Programmatically open the datetime picker
    e.currentTarget.showPicker?.();
  };

  const handleRescheduleCancel = () => {
    setShowRescheduleModal(false);
  };

  const handleCancel = () => {
    // Check if there's a previous page in location state
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }

    // Fallback to default navigation
    if (location.pathname.startsWith('/admin')) {
      if (airlinerId) {
        navigate(`/admin/airliners/${airlinerId}/flights`);
      } else {
        navigate('/admin/admin-flights');
      }
    } else {
      navigate('/airliner/flights');
    }
  };

  // Flight management table handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(filteredFlights.length / recordsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNum) => {
    console.log('📄 Page changed to:', pageNum);
    setCurrentPage(pageNum);
  };

  const handleEditFlight = (flightId) => {
    navigate(`/airliner/flights/${flightId}/edit`);
  };

  const handleDeleteFlight = (flightId) => {
    // Delete flight logic
  };

  // Filter flights based on search term (using allFlights from Redux)
  const filteredFlights = allFlights.filter(flight =>
    flight.flight_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    flight.aircraft?.registration_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + recordsPerPage, filteredFlights.length);
  const currentData = filteredFlights.slice(startIndex, endIndex);
  const totalRecords = filteredFlights.length;

  // Tooltip component
  const Tooltip = ({ id, text }) => (
    activeTooltip === id && (
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-[5px] px-[10px] bg-[#000] text-white text-base font-normal whitespace-nowrap rounded z-[10000] pointer-events-none leading-[1.42857143] after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[5px] after:border-transparent after:border-t-[#000]">
        {text}
      </span>
    )
  );

  // Styling classes
  const inputClass = "w-full h-[34px] py-[6px] px-3 text-sm leading-[1.42857143] text-[#555] bg-white border border-[#ccc] rounded shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] transition-[border-color_box-shadow] duration-150 ease-in-out placeholder:text-[#999] focus:outline-none focus:border-[#66afe9] focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.075),0_0_8px_rgba(102,175,233,0.6)]";
  const readOnlyInputClass = "w-full h-[34px] py-[6px] px-3 text-sm leading-[1.42857143] text-[#555] bg-[#eee] border border-[#ccc] rounded shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)]";
  const labelClass = "inline-block max-w-full mb-[5px] font-bold text-[13px] text-[#333]";
  const requiredClass = "text-red-600 text-sm align-baseline ml-1 font-bold";
  const errorClass = "text-[#a94442] block mt-[5px] text-sm";

  if (isEditMode && flightLoading) {
    return (
      <div className="p-0 bg-[#f5f8fc] min-h-screen w-full flex items-center justify-center">
        <div className="text-lg">Loading flight details...</div>
      </div>
    );
  }

  return (
    <div className="p-0 bg-[#f5f8fc] min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif]">
      {/* Header */}
      <div className="py-5 px-[30px] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-transparent">
        <h1 className="m-0 text-[18px] font-bold mb-[7px] font-['Bahnschrift_Light'] text-[#333]">
          {isEditMode ? 'Edit Flight' : 'Add Flight'}
        </h1>
        <button
          type="button"
          className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 hover:from-[#ffd200] hover:to-[#ffb300]"
          onClick={handleCancel}
        >
          <i className="fa fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="py-0 px-[30px] pb-[30px]">
        <form onSubmit={handleSubmit}>
          {/* Flight Details Section */}
          <section className="bg-white p-[20px] mb-5 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
            <h2 className="text-base font-bold m-0 mb-[20px] text-[#333] font-['Bahnschrift_Light']">
              Flight Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[20px] gap-y-[15px] mb-0">
              {/* Reg No */}
              <div className="flex flex-col">
                <label className={labelClass}>
                  Reg No. {!isEditMode && <span className={requiredClass}>*</span>}
                  {!isEditMode && (
                    <span className="relative inline-block ml-1">
                      <i
                        className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                        onMouseEnter={() => setActiveTooltip('regNo')}
                        onMouseLeave={() => setActiveTooltip(null)}
                      ></i>
                      <Tooltip id="regNo" text="Registration Number" />
                    </span>
                  )}
                </label>
                {isEditMode || aircraftId ? (
                  <input
                    type="text"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    readOnly
                    className={readOnlyInputClass}
                  />
                ) : (
                  <select
                    name="aircraftSelect"
                    value={selectedAircraftId}
                    onChange={handleAircraftChange}
                    className={inputClass}
                  >
                    <option value="">Select Aircraft</option>
                    {aircrafts && aircrafts.map((aircraft) => (
                      <option key={aircraft.id} value={aircraft.id}>
                        {aircraft.registration_number}
                      </option>
                    ))}
                  </select>
                )}
                {errors.registrationNumber && (
                  <span className={errorClass}>{errors.registrationNumber}</span>
                )}
              </div>

              {/* Flight No */}
              <div className="flex flex-col">
                <label className={labelClass}>
                  Flight No. <span className={requiredClass}>*</span>
                  <span className="relative inline-block ml-1">
                    <i
                      className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                      onMouseEnter={() => setActiveTooltip('flightNo')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip id="flightNo" text="Enter the operating Flight Number or Call Sign for this route." />
                  </span>
                </label>
                <input
                  type="text"
                  name="flightNumber"
                  placeholder="FLIGHT No."
                  value={formData.flightNumber}
                  onChange={handleChange}
                  className={`${inputClass} uppercase`}
                />
                {errors.flightNumber && (
                  <span className={errorClass}>{errors.flightNumber}</span>
                )}
              </div>
            </div>
          </section>

          {/* Route Details Section */}
          <section className="bg-white p-[10px] mb-5 rounded-sm shadow-[0_1px_1px_rgba(0,0,0,0.1)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[15px] gap-y-[15px] mb-[15px]">
              {/* Dep. Airport */}
              <div className="flex flex-col">
                <label className={labelClass}>
                  Dep. Airport <span className={requiredClass}>*</span>
                  <span className="relative inline-block ml-1">
                    <i
                      className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                      onMouseEnter={() => setActiveTooltip('depAirport')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip id="depAirport" text="Departure Airport" />
                  </span>
                </label>
                <AirportDropdown
                  name="departureAirport"
                  value={formData.departureAirport}
                  onChange={handleChange}
                  placeholder="Select Departure Airport"
                  error={errors.departureAirport}
                />
              </div>

              {/* ETD */}
              <div className="flex flex-col">
                <label className={labelClass}>
                  ETD <span className={requiredClass}>*</span>
                  <span className="relative inline-block ml-1">
                    <i
                      className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                      onMouseEnter={() => setActiveTooltip('etd')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip id="etd" text="Date / Time" />
                  </span>
                </label>
                <input
                  type="datetime-local"
                  name="departureDateTime"
                  value={formData.departureDateTime}
                  onChange={handleChange}
                  onClick={handleDateTimeClick}
                  min={getMinDateTime()}
                  className={`${inputClass} cursor-pointer`}
                />
                {errors.departureDateTime && (
                  <span className={errorClass}>{errors.departureDateTime}</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[15px] gap-y-[15px] mb-[15px]">
              {/* Arr. Airport */}
              <div className="flex flex-col">
                <label className={labelClass}>
                  Arr. Airport <span className={requiredClass}>*</span>
                  <span className="relative inline-block ml-1">
                    <i
                      className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                      onMouseEnter={() => setActiveTooltip('arrAirport')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip id="arrAirport" text="Arrival Airport" />
                  </span>
                </label>
                <AirportDropdown
                  name="arrivalAirport"
                  value={formData.arrivalAirport}
                  onChange={handleChange}
                  placeholder="Select Arrival Airport"
                  error={errors.arrivalAirport}
                />
              </div>

              {/* ETA */}
              <div className="flex flex-col">
                <label className={labelClass}>
                  ETA <span className={requiredClass}>*</span>
                  <span className="relative inline-block ml-1">
                    <i
                      className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                      onMouseEnter={() => setActiveTooltip('eta')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip id="eta" text="Arrival Date Time" />
                  </span>
                </label>
                <input
                  type="datetime-local"
                  name="arrivalDateTime"
                  value={formData.arrivalDateTime}
                  onChange={handleChange}
                  onClick={handleDateTimeClick}
                  min={getMinDateTime()}
                  className={`${inputClass} cursor-pointer`}
                />
                {errors.arrivalDateTime && (
                  <span className={errorClass}>{errors.arrivalDateTime}</span>
                )}
                {errors.endurance && (
                  <span className={errorClass}>{errors.endurance}</span>
                )}
              </div>
            </div>

            {/* Dynamic Price Rows based on BEL Checkbox */}
            {!formData.blockEmptyLeg ? (
              <>
                {/* Row: EL Price | Total Seats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[15px] gap-y-[15px] mb-[15px]">
                  {/* EL Price */}
                  <div className="flex flex-col">
                    <label className={labelClass}>
                      EL Price <span className={requiredClass}>*</span>
                      <span className="relative inline-block ml-1">
                        <i
                          className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                          onMouseEnter={() => setActiveTooltip('elPrice')}
                          onMouseLeave={() => setActiveTooltip(null)}
                        ></i>
                        <Tooltip id="elPrice" text="Enter the total price for this empty leg in increments of $100. E.g., 10000, 10100, etc." />
                      </span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-[7px] text-[#666] text-sm">$</span>
                      <input
                        type="number"
                        name="blockPrice"
                        placeholder="EL Price"
                        value={formData.blockPrice}
                        onChange={handleChange}
                        step="0.01"
                        className={`${inputClass} pl-8`}
                      />
                    </div>
                    {errors.blockPrice && (
                      <span className={errorClass}>{errors.blockPrice}</span>
                    )}
                  </div>

                  {/* Seat(s) */}
                  <div className="flex flex-col">
                    <label className={labelClass}>
                      Seat(s) <span className={requiredClass}>*</span>
                      <span className="relative inline-block ml-1">
                        <i
                          className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                          onMouseEnter={() => setActiveTooltip('seats')}
                          onMouseLeave={() => setActiveTooltip(null)}
                        ></i>
                        <Tooltip id="seats" text="Enter the total number of seats available for this flight." />
                      </span>
                    </label>
                    <input
                      type="number"
                      name="seatAvailable"
                      placeholder="Total Seats"
                      value={formData.seatAvailable}
                      onChange={handleChange}
                      min="1"
                      step="1"
                      className={inputClass}
                    />
                    {errors.seatAvailable && (
                      <span className={errorClass}>{errors.seatAvailable}</span>
                    )}
                  </div>
                </div>

                {/* Row: Seat Price | BEL Checkbox */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[15px] gap-y-[15px] mb-[15px]">
                  {/* Seat Price */}
                  <div className="flex flex-col">
                    <label className={labelClass}>
                      Seat Price
                      <span className="relative inline-block ml-1">
                        <i
                          className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                          onMouseEnter={() => setActiveTooltip('seatPrice')}
                          onMouseLeave={() => setActiveTooltip(null)}
                        ></i>
                        <Tooltip id="seatPrice" text="Automatically calculated based on the number of Seat(s) Available." />
                      </span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-[7px] text-[#666] text-sm">$</span>
                      <input
                        type="text"
                        name="seatPrice"
                        value={formData.seatPrice}
                        readOnly
                        className={`${readOnlyInputClass} pl-8`}
                      />
                    </div>
                  </div>

                  {/* Block Empty Leg Checkbox (Unchecked) */}
                  <div className="flex items-end pb-[7px]">
                    <label className="flex items-center cursor-pointer font-normal gap-2 mb-0">
                      <input
                        type="checkbox"
                        name="blockEmptyLeg"
                        checked={formData.blockEmptyLeg}
                        onChange={handleBlockEmptyLegChange}
                        className="w-4 h-4 cursor-pointer accent-[#333]"
                      />
                      <span className="text-sm font-bold text-[#333]">Block Empty Leg (BEL) Price</span>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Row: BEL Price | BEL Checkbox (Checked) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[15px] gap-y-[15px] mb-[15px]">
                  {/* BEL Price */}
                  <div className="flex flex-col">
                    <label className={labelClass}>
                      BEL Price <span className={requiredClass}>*</span>
                      <span className="relative inline-block ml-1">
                        <i
                          className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                          onMouseEnter={() => setActiveTooltip('belPrice')}
                          onMouseLeave={() => setActiveTooltip(null)}
                        ></i>
                        <Tooltip id="belPrice" text="Enter the total price for this empty leg." />
                      </span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-[7px] text-[#666] text-sm">$</span>
                      <input
                        type="number"
                        name="blockPrice"
                        placeholder="BEL Price"
                        value={formData.blockPrice}
                        onChange={handleChange}
                        step="0.01"
                        className={`${inputClass} pl-8`}
                      />
                    </div>
                    {errors.blockPrice && (
                      <span className={errorClass}>{errors.blockPrice}</span>
                    )}
                  </div>

                  {/* Block Empty Leg Checkbox (Checked) */}
                  <div className="flex items-end pb-[7px]">
                    <label className="flex items-center cursor-pointer font-normal gap-2 mb-0">
                      <input
                        type="checkbox"
                        name="blockEmptyLeg"
                        checked={formData.blockEmptyLeg}
                        onChange={handleBlockEmptyLegChange}
                        className="w-4 h-4 cursor-pointer accent-[#333]"
                      />
                      <span className="text-sm font-bold text-[#333]">Block Empty Leg (BEL) Price</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Flight Time (Always separate row) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[15px] gap-y-[15px] mb-[15px]">
              <div className="flex flex-col">
                <label className={labelClass}>
                  Flight Time
                  <span className="relative inline-block ml-1">
                    <i
                      className="fa fa-info-circle text-[#555] cursor-pointer text-base"
                      onMouseEnter={() => setActiveTooltip('flightTime')}
                      onMouseLeave={() => setActiveTooltip(null)}
                    ></i>
                    <Tooltip id="flightTime" text="Flight Time" />
                  </span>
                </label>
                <input
                  type="text"
                  name="flightTime"
                  value={formData.flightTime}
                  readOnly
                  className={readOnlyInputClass}
                />
                {errors.flightTime && (
                  <span className={errorClass}>{errors.flightTime}</span>
                )}
              </div>
              <div className="hidden md:block"></div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-2.5 mt-0">
            {isEditMode && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleReschedule}
                className="text-black h-9 w-full sm:w-auto py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 hover:from-[#ffd200] hover:to-[#ffb300] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Rescheduling..." : "Reschedule Flight"}
              </button>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="text-black h-9 w-full sm:w-auto py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 hover:from-[#ffd200] hover:to-[#ffb300] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Adding..."
                : isEditMode
                  ? "Save"
                  : "Add Flight"}
            </button>

             {!isEditMode && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={(e) => handleSubmit(e, true)}
                className="text-black h-9 w-full sm:w-auto py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 hover:from-[#ffd200] hover:to-[#ffb300] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            )}


            {/* Cancel Button */}
            <button
              type="button"
              onClick={handleCancel}
              className="text-[#333] h-9 w-full sm:w-auto py-2 px-4 text-sm bg-white border border-[#ccc] rounded cursor-pointer font-normal transition-all duration-200 hover:bg-[#e6e6e6] hover:border-[#adadad]"
            >
              Cancel
            </button>
          </div>

        </form>

        {/* Confirmation Modal (before adding/editing flight) */}
        <UniversalConfirmModal
          isOpen={showConfirmModal}
          onConfirm={handleConfirmSubmit}
          onClose={() => {
            setShowConfirmModal(false);
            setPendingSubmitData(null);
          }}
          type="successconfig"
          title="Are you sure?"
          message={`You are about to ${isEditMode ? 'EDIT' : 'ADD'} this Flight. Click OK to proceed, or CANCEL to exit.`}
          confirmText={isEditMode ? 'EDIT' : 'ADD'}
          cancelText="CANCEL"
          theme="modern"
        />

        {/* Reschedule Confirmation Modal (only in edit mode) */}
        {isEditMode && (
          <UniversalConfirmModal
            isOpen={showRescheduleModal}
            onConfirm={handleRescheduleConfirm}
            onClose={handleRescheduleCancel}
            type="successconfig"
            title="Are you sure?"
            message="You are about to RESCHEDULE this flight. Click OK to proceed or CANCEL to exit."
            theme="modern"
          />
        )}

        {
          !isEditMode && (
            <>
              {/* Flight(s) Listed Table */}
              <FlightManagementTable
                flights={filteredFlights}
                loading={flightLoading}
                error={flightError}
                searchTerm={searchTerm}
                onSearchChange={handleSearch}
                recordsPerPage={recordsPerPage}
                onRecordsPerPageChange={handleRecordsPerPageChange}
                currentPage={currentPage}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
                onPageChange={handlePageChange}
                visibleColumns={visibleColumns}
                onViewFlight={() => { }}
                onEditFlight={handleEditFlight}
                onDeleteFlight={handleDeleteFlight}
                onCancelFlight={() => { }}
                onRestoreFlight={() => { }}
                onPermanentDeleteFlight={() => { }}
                showActionColumn={false}
              />
            </>
          )
        }
      </div>
    </div>
  );
};

export default FlightFormPage;
